/**
 * AuthContext — OM Yoga UI (client: radhekrishna)
 * Mirrors the om_estate_ui AuthContext pattern but scoped to yoga sessions.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import {
  type YogaUser,
  getSavedUser,
  saveSession,
  clearSession,
  login as apiLogin,
  registerYogaUser,
  type RegisterRequest,
} from '@/lib/api'

interface AuthState {
  user: YogaUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, mfaCode?: string) => Promise<void>
  register: (body: RegisterRequest) => Promise<void>
  logout: () => void
  /** Hydrate auth state from an already-issued token (e.g. after auto-registration). */
  autoLogin: (token: string, user: YogaUser) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  })

  // Restore session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('yoga_token')
    const storedUser = getSavedUser()
    if (storedToken && storedUser && !isTokenExpired(storedToken)) {
      setState({ user: storedUser, token: storedToken, isAuthenticated: true, isLoading: false })
    } else {
      clearSession()
      setState(s => ({ ...s, isLoading: false }))
    }
  }, [])

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const resp = await apiLogin(email, password, mfaCode)
    const user = resp.user ?? null
    if (user) {
      saveSession(resp.access_token, user)
      setState({ user, token: resp.access_token, isAuthenticated: true, isLoading: false })
    } else {
      // Server returned token but no user — decode from token
      const token = resp.access_token
      saveSession(token, { email } as YogaUser)
      setState({ user: { email } as YogaUser, token, isAuthenticated: true, isLoading: false })
    }
  }, [])

  const register = useCallback(async (body: RegisterRequest) => {
    const resp = await registerYogaUser(body)
    saveSession(resp.access_token, resp.user)
    setState({ user: resp.user, token: resp.access_token, isAuthenticated: true, isLoading: false })
  }, [])

  const logout = useCallback(() => {
    clearSession()
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false })
  }, [])

  const autoLogin = useCallback((token: string, user: YogaUser) => {
    saveSession(token, user)
    setState({ user, token, isAuthenticated: true, isLoading: false })
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, autoLogin }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
